import { type ColumnDef } from "@tanstack/react-table-v9";
import { useTranslation } from "react-i18next";

import { spacing } from "#app/theme/v2/spacing";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Tooltip } from "#components/atoms/Tooltip/Tooltip";
import { Select } from "#components/molecules/Select/Select";
import { type TableAppFeatures } from "#components/organisms/Table/tableHook";
import { MaterialsVolumetricPriceDto } from "#modules/materials/infra/fetchMaterialsVolumetricPrice.api";
import { NameCell } from "#modules/onboardingV2/view/volumetricMaterialSpecification/NameCell";
import { PriceCell } from "#modules/onboardingV2/view/volumetricMaterialSpecification/PriceCell";
import { Icon } from "#shared/icons/Icon";
import { MaterialGradeSchema, MaterialGradeType } from "shared";

export type MaterialRow = MaterialsVolumetricPriceDto[number];

export type MaterialRowChanges = Partial<
  Pick<MaterialRow, "materialGrade" | "name" | "volumetricPrice">
>;

export const useVolumetricMaterialSpecificationColumns = (
  priceColumnLabel: string,
  onCommitRowChange: (row: MaterialRow, changes: MaterialRowChanges) => void,
  onDeleteRow: (row: MaterialRow) => void,
) => {
  const { t } = useTranslation();

  const materialGradeOptions = Object.values(MaterialGradeSchema.enum)
    .map((grade) => ({ value: grade, label: t(`qrl.materialGrades.${grade}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const columns: ColumnDef<TableAppFeatures, MaterialRow>[] = [
    {
      accessorKey: "materialGrade",
      header: t("qrl.materialGrade"),
      sortFn: (rowA, rowB) =>
        t(`qrl.materialGrades.${rowA.original.materialGrade}`).localeCompare(
          t(`qrl.materialGrades.${rowB.original.materialGrade}`),
        ),
      cell: ({ row }) => (
        <Select
          value={row.original.materialGrade}
          onChange={(value) =>
            value && onCommitRowChange(row.original, { materialGrade: value as MaterialGradeType })
          }
          data={materialGradeOptions}
        />
      ),
    },
    {
      accessorKey: "name",
      header: t("qrl.materialSpecification"),
      enableSorting: false,
      cell: ({ row }) => (
        <NameCell
          value={row.original.name}
          ariaLabel={t("qrl.materialSpecification")}
          onUpdate={(name) => onCommitRowChange(row.original, { name })}
        />
      ),
    },
    {
      accessorKey: "volumetricPrice",
      header: priceColumnLabel,
      size: 266,
      enableSorting: false,
      cell: ({ row }) => (
        <PriceCell
          value={row.original.volumetricPrice}
          ariaLabel={priceColumnLabel}
          onUpdate={(volumetricPrice) => onCommitRowChange(row.original, { volumetricPrice })}
        />
      ),
    },
    {
      id: "delete",
      header: () => null,
      meta: {
        verticalPadding: spacing[0],
        horizontalPadding: spacing[0],
      },
      size: 32,
      enableSorting: false,
      cell: ({ row }) => (
        <Tooltip label={t("common.delete")}>
          <IconButton
            variant="transparent"
            size="sm"
            aria-label={t("common.delete")}
            onClick={() => onDeleteRow(row.original)}
          >
            <Icon name="trash" size={16} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return columns;
};
